/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * Leases — GET /re/leases (status, project_id, expiring_within_days, search); New lease → POST /re/leases.
 *
 * ?application_id= (v2 Convert of a rental application from the customer
 * portal) opens New lease filled from GET /re/applications/{id}/conversion;
 * after the agent saves, the lease is linked back (POST .../link). Without it
 * nothing here changes.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Icon, ToggleButton, ToggleButtonGroup } from '@mui/material';
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import { usePermission } from 'auth/usePermission';
import useDebounce from 'hooks/useDebounce';
import RePage, { FilterBar, LoadError, RePanel, ReSearch, useSnack } from './components/RePage';
import ReStatusChip from './components/ReStatusChip';
import ReTable from './components/ReTable';
import { RePicker, VocabSelect } from './components/Pickers';
import { Num } from './components/Widgets';
import { NewLeaseDialog } from './components/LeaseDialogs';
import {
  LEASE_STATUSES, errorMessage, formatDate, formatMoney, humanize, useReApi, useReGet, useReList,
} from './api';
import { isAustralia } from '../variant';
import { useIsReV2 } from '../v2/parts';
import { FilterChip } from 'components/v2/filters';

const EXPIRING = [['', 'All'], ['30', 'Expiring 30d'], ['60', '60d'], ['90', '90d']];

const daysLeft = (end) => {
  if (!end) return null;
  const [y, m, d] = end.split('-').map(Number);
  const ms = new Date(y, m - 1, d).getTime() - new Date().setHours(0, 0, 0, 0);
  return Math.round(ms / 86400000);
};

export default function LeasesList() {
  const isV2 = useIsReV2();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [snack, notify] = useSnack();
  const { canCreate } = usePermission('re_leases');
  const [status, setStatus] = useState(null);
  const [projectId, setProjectId] = useState(null);
  const [expiring, setExpiring] = useState(params.get('expiring_within_days') || '');
  const [search, setSearch] = useState('');
  const debounced = useDebounce(search, 350);
  const [open, setOpen] = useState(false);
  const { api } = useReApi();
  const applicationId = params.get('application_id') ? Number(params.get('application_id')) : null;
  const conversion = useReGet(applicationId ? `/re/applications/${applicationId}/conversion` : null);
  const prefill = conversion.data?.target === 'lease' ? conversion.data.prefill : null;
  useEffect(() => {
    if (prefill && canCreate) setOpen(true);
  }, [prefill, canCreate]);
  const list = useReList('/re/leases', {
    status, project_id: projectId, expiring_within_days: expiring, search: debounced,
    sort_by: expiring ? 'end_date' : 'start_date', sort_order: expiring ? 'asc' : 'desc',
  });

  return (
    <RePage
      title="Leases"
      subtitle={isAustralia() ? 'Leases, bonds and rent' : 'Tenancy contracts, Ejari and rent cheques'}
      actions={canCreate && (
        <MDButton variant="gradient" color="info" size="small" onClick={() => setOpen(true)}>
          <Icon>add</Icon>&nbsp;New lease
        </MDButton>
      )}
    >
      <FilterBar>
        <ReSearch label="Search lease no. / tenant / unit" value={search} onChange={setSearch} sx={{ minWidth: 240 }} />
        <VocabSelect label="Status" value={status} onChange={setStatus} values={LEASE_STATUSES} />
        <RePicker path="/re/projects" label="Project" value={projectId} onChange={setProjectId} />
        {isV2 ? (
          // Same exclusive toggle: picking the selected one again clears it.
          <MDBox role="group" aria-label="Expiring" display="flex" gap={0.75} flexWrap="wrap">
            {EXPIRING.map(([v, text]) => (
              <FilterChip key={v || 'all'} label={text} selected={expiring === v} onClick={() => setExpiring(expiring === v ? '' : v)} />
            ))}
          </MDBox>
        ) : (
          <ToggleButtonGroup exclusive size="small" value={expiring} onChange={(e, v) => setExpiring(v ?? '')}>
            <ToggleButton value="">All</ToggleButton>
            <ToggleButton value="30">Expiring 30d</ToggleButton>
            <ToggleButton value="60">60d</ToggleButton>
            <ToggleButton value="90">90d</ToggleButton>
          </ToggleButtonGroup>
        )}
      </FilterBar>
      {applicationId && conversion.error && (
        <LoadError error={conversion.error} onRetry={conversion.reload} />
      )}
      <LoadError error={list.error} onRetry={list.reload} />
      <RePanel p={0}>
        <ReTable
          loading={list.loading}
          rows={list.items}
          emptyMessage="No leases match these filters."
          onRowClick={(l) => navigate(`/real-estate/leases/${l.id}`)}
          total={list.total}
          page={list.page}
          pageSize={list.pageSize}
          onPageChange={list.setPage}
          onPageSizeChange={list.setPageSize}
          columns={[
            { key: 'lease_number', label: 'Lease', render: (l) => <strong>{l.lease_number || `#${l.id}`}</strong> },
            { key: 'unit', label: 'Unit', render: (l) => [l.unit_number, l.project_name].filter(Boolean).join(' · ') || '—' },
            { key: 'tenant', label: 'Tenant', render: (l) => l.tenant_name || l.contact_name || '—' },
            { key: 'start_date', label: 'Start', render: (l) => formatDate(l.start_date) },
            {
              key: 'end_date', label: 'End',
              render: (l) => {
                const d = daysLeft(l.end_date);
                const warn = d !== null && d >= 0 && d <= 90 && ['ACTIVE', 'RENEWAL_DUE'].includes(l.status);
                return (
                  <span>
                    {formatDate(l.end_date)}
                    {warn && <Num bold color={d <= 30 ? '#BA1A1A' : '#8A5300'}>{` · ${d}d`}</Num>}
                  </span>
                );
              },
            },
            { key: 'annual_rent', label: 'Annual rent', align: 'right', render: (l) => <Num>{formatMoney(l.annual_rent, l.currency, { decimals: 0 })}</Num> },
            ...(isAustralia() ? [
              { key: 'rent_frequency', label: 'Rent', render: (l) => humanize(l.rent_frequency) },
              { key: 'bond_amount', label: 'Bond', align: 'right', render: (l) => formatMoney(l.bond_amount, l.currency, { decimals: 0 }) },
            ] : [
              { key: 'cheques', label: 'Cheques', render: (l) => `${l.number_of_cheques} · ${humanize(l.rent_frequency)}` },
              { key: 'ejari_no', label: 'Ejari', render: (l) => l.ejari_no || '—' },
            ]),
            { key: 'status', label: 'Status', render: (l) => <ReStatusChip vocab="lease" status={l.status} size="xs" /> },
          ]}
        />
      </RePanel>
      <NewLeaseDialog
        open={open}
        onClose={() => setOpen(false)}
        prefill={prefill}
        onCreated={async (lease) => {
          setOpen(false);
          if (applicationId && prefill) {
            try {
              await api.post(`/re/applications/${applicationId}/link`, { lease_id: lease.id });
            } catch (e) {
              // The lease is saved; the application page offers to link it again.
              navigate(`/real-estate/applications/${applicationId}`, {
                state: { linkError: errorMessage(e, 'The application could not be marked converted.'), leaseId: lease.id },
              });
              return;
            }
          }
          notify('Lease created');
          navigate(`/real-estate/leases/${lease.id}`);
        }}
      />
      {snack}
    </RePage>
  );
}
